/**
 * Replies — where they come from and the ones the proxy could not place. LinkedIn's notification
 * mails in the recruiter's own Gmail are the automatic source (Lever does not notice a reply);
 * "They replied" on the card and a re-export in Recruiter are the two manual ones.
 */
import { showToast } from '@finn-auto/design-system/atoms/toast';
import * as api from '../../services/taOutreachApi';
import type { OutreachCandidate } from '../../types/taOutreach';

export function RepliesPanel({
  postingId,
  sent,
  gmailConnected,
  onConnectGmail,
  mailCheck,
  unmatched,
  onCheckNow,
  onReplied,
  onIgnored,
}: {
  postingId: string;
  sent: OutreachCandidate[];
  gmailConnected: boolean;
  onConnectGmail: () => void;
  mailCheck: { at: number; error?: string } | null;
  unmatched: api.ReplyNotification[];
  onCheckNow: () => void;
  onReplied: (item: OutreachCandidate, threadId: string | null) => void;
  onIgnored: (threadId: string | null) => void;
}) {
  const replied = sent.filter((c) => c.reply).length;
  return (
    <div className="space-y-3">
      <div className="rounded-md border border-solid border-[#E9EAEC] bg-[#F8F8F8] px-[18px] py-[13px] text-[13px]">
        {gmailConnected ? (
          <>
            Replies are read from LinkedIn’s notification mails in your Gmail
            {mailCheck ? (mailCheck.error ? ` — last check failed: ${mailCheck.error}` : ` — checked ${new Date(mailCheck.at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`) : ''}.
            In Recruiter, keep email notifications for messages and InMails ON (Product settings → Notifications → “Message and InMail”, frequency
            “Individually”).{' '}
            <button type="button" className="font-semibold text-[#0072EA] underline" onClick={onCheckNow}>
              Check now
            </button>
          </>
        ) : (
          <>
            Connect Google and replies are read from LinkedIn’s notification mails in your Gmail — nothing on linkedin.com is touched.{' '}
            <button type="button" className="font-semibold text-[#0072EA] underline" onClick={onConnectGmail}>
              Connect Google
            </button>
          </>
        )}
      </div>
      {unmatched.length > 0 && (
        <div className="space-y-2 rounded-md border border-solid border-[#F5C26B] bg-[#FFF8E6] px-[18px] py-[13px] text-[13px]">
          <b>LinkedIn notifications we could not place</b> — whose reply is this?
          {unmatched.map((n) => (
            <div key={n.threadId || n.messageId || n.at || n.subject} className="flex flex-wrap items-center gap-2 rounded-md bg-white px-3 py-2">
              <span className="min-w-0 flex-1">
                <span className="block font-medium">{n.subject}</span>
                <span className="block text-[#707070]">
                  {n.at ? new Date(n.at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                  {n.preview ? ` · “${n.preview}”` : ''}
                </span>
              </span>
              {n.gmailUrl && (
                <a href={n.gmailUrl} target="_blank" rel="noreferrer" className="text-[#0072EA] underline">
                  Gmail ↗
                </a>
              )}
              <select
                className="h-8 rounded-md border border-solid border-[#E9EAEC] bg-white px-2"
                defaultValue=""
                aria-label="Whose reply is this?"
                onChange={(e) => {
                  const id = e.target.value;
                  if (!id) return;
                  void api
                    .placeReply(id, { gmailThreadId: n.threadId, preview: n.preview, at: n.at })
                    .then((r) => onReplied(r.item, n.threadId))
                    .catch((err) => showToast({ status: 'error', position: 'bottomCenter', description: err instanceof Error ? err.message : 'Could not record that.' }));
                }}
              >
                <option value="">Reply from…</option>
                {sent
                  .filter((c) => !c.reply)
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
              </select>
              <button
                type="button"
                className="h-8 rounded-md px-2.5 text-xs font-semibold text-[#707070] hover:bg-[#F8F8F8] hover:text-[#1A1A1A]"
                onClick={() =>
                  void api
                    .ignoreReplyMail(postingId, n.threadId || '')
                    .then(() => onIgnored(n.threadId))
                    .catch(() => {})
                }
              >
                Not a reply
              </button>
            </div>
          ))}
        </div>
      )}
      {sent.filter((c) => c.reply).map((c) => (
        <div key={c.id} className="flex flex-wrap items-center gap-3 rounded-md border border-solid border-[#E9EAEC] bg-white px-[22px] py-[14px] text-[13px]">
          <b>{c.name}</b>
          <span className="text-[#707070]">
            replied {c.reply && new Date(c.reply.at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
            {c.reply?.source === 'lever' ? ' · from Lever' : c.reply?.source === 'gmail' ? ' · LinkedIn notification' : ' · recorded by you'}
          </span>
          {c.reply?.preview && <span className="basis-full text-[#1A1A1A]">“{c.reply.preview}”</span>}
          {c.lever?.url && (
            <a href={c.lever.url} target="_blank" rel="noreferrer" className="text-[#0072EA] underline">
              Open in Lever ↗
            </a>
          )}
        </div>
      ))}
      {sent.length > 0 && !replied && (
        <p className="text-[13px] text-[#707070]">
          No replies registered yet. A reply lands here from LinkedIn’s notification mail (checked every five minutes while this campaign is
          open), from “They replied” on the card, or when you export the profile again in Recruiter after the answer.
        </p>
      )}
    </div>
  );
}
